import { Suspense, useState, useCallback, useEffect, useRef } from "react";
import { useSearch } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild,
} from "@headlessui/react";
import {
  XMarkIcon,
  TrashIcon,
  ArrowsUpDownIcon,
} from "@heroicons/react/24/outline";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { useRelocationPageActions } from "@/app/Settings/Admin/RelocationPages/hooks/useRelocationPageActions";
import { ActionGroupButtons } from "@/lib/Components/Actions/ActionGroupButtons";
import { RecordScreenFormCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenFormCard";
import { RecordScreenCard } from "@/lib/Components/Screens/RecordScreen/Cards/RecordScreenCard";
import { updateRelocationPageMutation } from "@/app/Settings/Admin/RelocationPages/queries/relocationPageMutations";
import { UpdateRelocationPageInput, ModelType } from "@/gql/graphql";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { useRelocationPageDrawer } from "@/app/Settings/Admin/RelocationPages/Drawer/useRelocationPageDrawer";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { RelocationPageTabs } from "@/app/Settings/Admin/RelocationPages/Drawer/RelocationPageDrawerTabs";
import { MediaCollection } from "@/gql/graphql";
import { ImoovaImage } from "@/app/Media/Components/Image";
import { BookOpenIcon, BookMinusIcon } from "lucide-react";
import { FileUploader, Upload } from "@/lib/FileUpload/FileUploader";
import { createMediaMutation } from "@/app/Media/GraphQL/mediaMutations";
import { SortableList } from "@/lib/Components/Sortable/SortableList";
import {
  SortableItem,
  SortableItemDragHandle,
} from "@/lib/Components/Sortable/SortableItem";
import { IconButton } from "@/lib/Components/Button/IconButton";
import { EmptyState } from "@/lib/Components/EmptyState/EmptyState";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { blogPostListQuery } from "@/app/Blogs/GraphQL/blogPostListQuery";
import {
  createRelocationPageArticleMutation,
  deleteRelocationPageArticleMutation,
  sortRelocationPageArticleMutation,
} from "@/app/Settings/Admin/RelocationPages/GraphQL/relocationPageArticleMutations";
import { useCloseSearchDrawer } from "@/lib/Hooks/useCloseSearchDrawer";

export function RelocationPageDrawer() {
  const search = useSearch({
    from: "/$tenant/$orgId/supplier/{-$supplierId}",
  });
  const [activeTab, setActiveTab] = useState("details");

  const isOpen = !!search.relocationPageId;

  const handleClose = useCloseSearchDrawer("relocationPageId");

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative">
      <DialogBackdrop
        transition
        className="data-closed:opacity-0 fixed inset-0 bg-gray-900/50 transition-opacity duration-500 ease-in-out"
      />

      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-6 right-6 flex max-w-full pl-10 sm:pl-16">
            <DialogPanel
              transition
              className="data-closed:translate-x-full pointer-events-auto relative w-screen max-w-4xl transform transition duration-500 ease-in-out sm:duration-700"
            >
              <TransitionChild>
                <div className="data-closed:opacity-0 absolute left-0 top-0 -ml-8 flex pr-2 pt-4 duration-500 ease-in-out sm:-ml-10 sm:pr-4">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="relative rounded-md text-gray-400 hover:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                  >
                    <span className="absolute -inset-2.5" />
                    <span className="sr-only">Close panel</span>
                    <XMarkIcon aria-hidden="true" className="size-6" />
                  </button>
                </div>
              </TransitionChild>

              {search.relocationPageId ? (
                <div className="relative h-full overflow-hidden rounded-2xl bg-white shadow-xl">
                  <div className="flex h-full flex-col overflow-hidden">
                    <Suspense
                      fallback={
                        <div className="flex h-full items-center justify-center">
                          <Spinner />
                        </div>
                      }
                    >
                      <DialogHeading
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                      />

                      <div className="flex-1 overflow-y-auto px-6 py-6">
                        <TabContent activeTab={activeTab} />
                      </div>
                    </Suspense>
                  </div>
                </div>
              ) : null}
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

function TabContent({ activeTab }: { activeTab: string }) {
  switch (activeTab) {
    case "details":
      return <RelocationPageDetailsTab />;
    case "articles":
      return <RelocationPageArticlesTab />;
    case "image":
      return <RelocationPageImageTab />;
    default:
      throw new Error("Unknown Tab");
  }
}

function DialogHeading({
  activeTab,
  setActiveTab,
}: {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}) {
  const relocationPage = useRelocationPageDrawer();

  if (!relocationPage) {
    return null;
  }

  return (
    <div className="sticky top-0 z-10 border-b border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between px-6 py-5">
        <div className="flex items-center gap-4">
          {relocationPage.image ? (
            <ImoovaImage
              media={relocationPage.image}
              alt={relocationPage.title}
              className="h-14 w-14 rounded-lg object-cover ring-1 ring-gray-200"
            />
          ) : null}
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {relocationPage.title}
            </h2>
            <p className="text-sm text-gray-500">{relocationPage.slug}</p>
          </div>
        </div>

        <RelocationPageDrawerActions relocationPage={relocationPage} />
      </div>

      <div className="px-6">
        <RelocationPageTabs activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>
    </div>
  );
}

function RelocationPageDetailsTab() {
  const relocationPage = useRelocationPageDrawer();
  const { mutateAsync } = useGqlMutation(updateRelocationPageMutation);

  if (!relocationPage) {
    return null;
  }

  const pageId = relocationPage.id;

  async function handleSubmit(values: UpdateRelocationPageInput) {
    await mutateAsync({
      id: pageId,
      input: values,
    });
  }

  return (
    <div className="space-y-8">
      <RecordScreenFormCard<UpdateRelocationPageInput>
        title="Basic Information"
        initialValues={{
          title: relocationPage.title,
          slug: relocationPage.slug,
          description: relocationPage.description,
          region: relocationPage.region,
        }}
        onSubmit={handleSubmit}
      >
        <TextInput name="title" label="Title" />
        <TextInput name="slug" label="Slug" />
        <TextInput name="region" label="Region" optionalLabel />
        <div className="col-span-full">
          <TextAreaInput name="description" label="Description" optionalLabel />
        </div>
      </RecordScreenFormCard>
    </div>
  );
}

type Article = {
  id: string;
  slug: string;
  seo: {
    title: string | null;
    image: {
      url: string | null;
      alt: string | null;
    };
  };
};

function RelocationPageArticlesTab() {
  const relocationPage = useRelocationPageDrawer();
  const queryClient = useQueryClient();
  const [items, setItems] = useState(relocationPage?.articles || []);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (relocationPage?.articles) {
      setItems(relocationPage.articles);
    }
  }, [relocationPage?.articles?.length]);

  const { mutateAsync } = useGqlMutation(sortRelocationPageArticleMutation, {
    onSuccess: () => {
      return;
    },
  });

  const { mutateAsync: deleteItem } = useGqlMutation(
    deleteRelocationPageArticleMutation,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (q) => q.queryKey.includes("RelocationPageRecord"),
        });
      },
    },
  );

  const handleSort = useCallback(
    (articles: any[]) => {
      setItems(articles);
      return mutateAsync({
        ids: articles.map((item) => item.id),
      });
    },
    [mutateAsync],
  );

  const handleDelete = useCallback(
    (id: string) => {
      return deleteItem({ id });
    },
    [deleteItem],
  );

  const { data: blogPostsData } = useGqlQuery(blogPostListQuery, {
    first: 500,
    publishedOnly: true,
  });

  const datoArticles = blogPostsData
    ? {
        data: {
          allArticles: blogPostsData.blogPosts.data.map((post) => ({
            id: post.id,
            slug: post.slug,
            seo: {
              title: post.title,
              image: {
                url: post.hero_image?.url ?? "",
                alt: post.title,
              },
            },
          })),
          _allArticlesMeta: {
            count: blogPostsData.blogPosts.paginatorInfo.total,
          },
        },
      }
    : undefined;

  const filteredArticles = datoArticles?.data.allArticles.filter((article) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      article.seo.title?.toLowerCase().includes(query) ||
      article.slug.toLowerCase().includes(query)
    );
  });

  if (!relocationPage) {
    return null;
  }

  return (
    <div className="space-y-10">
      <RecordScreenCard
        title={
          <div className="flex items-center gap-2 text-indigo-700">
            <BookOpenIcon className="h-5 w-5" />
            <span>Attached Articles</span>
            <span className="ml-1 rounded-full bg-indigo-100 px-2.5 py-0.5 text-sm font-medium text-indigo-700">
              {items.length}
            </span>
          </div>
        }
      >
        {items.length === 0 ? (
          <EmptyState
            Icon={BookMinusIcon}
            header="No articles attached"
            subheader="Add articles to this relocation page"
            className="rounded-lg bg-gray-50 py-12"
          />
        ) : (
          <div className="rounded-lg bg-gray-50 p-4">
            {items.length > 1 && (
              <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
                <ArrowsUpDownIcon className="h-4 w-4" />
                <span>Drag to reorder articles</span>
              </div>
            )}
            <SortableList
              items={items}
              onChange={handleSort}
              renderItem={(item) => {
                const article = datoArticles?.data.allArticles.find(
                  (a) => a.slug === item.dato_article_slug,
                );

                return (
                  <SortableItem id={item.id}>
                    <div className="group relative w-full overflow-hidden rounded-lg border border-gray-200 bg-white">
                      <div className="invisible absolute left-0 top-0 group-hover:visible">
                        <IconButton
                          Icon={TrashIcon}
                          onClick={() => handleDelete(item.id)}
                          tooltip="Delete"
                          className="hover:bg-red-50"
                        />
                      </div>

                      <div className="flex items-center">
                        <div className="h-20 w-20 shrink-0">
                          {article?.seo.image.url ? (
                            <img
                              loading="lazy"
                              className="h-full w-full object-cover"
                              src={article.seo.image.url}
                              alt={article.seo.title ?? ""}
                            />
                          ) : null}
                        </div>
                        <div className="flex-1 p-3">
                          <p className="line-clamp-2 font-medium text-gray-800">
                            {article?.seo.title ?? item.dato_article_slug}
                          </p>
                          <p className="text-xs text-gray-500">
                            {item.dato_article_slug}
                          </p>
                        </div>
                      </div>

                      <SortableItemDragHandle>
                        <div className="absolute inset-y-0 right-2 flex w-[12px] cursor-grab items-center justify-center text-gray-400 hover:text-gray-600">
                          <svg
                            viewBox="0 0 20 20"
                            width="12"
                            fill="currentColor"
                            className="flex h-full overflow-visible"
                          >
                            <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z"></path>
                          </svg>
                        </div>
                      </SortableItemDragHandle>
                    </div>
                  </SortableItem>
                );
              }}
            />
          </div>
        )}
      </RecordScreenCard>

      <RecordScreenCard
        title={
          <div className="flex items-center gap-2 text-emerald-700">
            <BookOpenIcon className="h-5 w-5" />
            <span>Published Articles</span>
          </div>
        }
      >
        <div className="space-y-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search articles by title or slug..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
            {searchQuery ? (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            ) : null}
          </div>

          {filteredArticles && filteredArticles.length > 0 ? (
            <VirtualizedArticleGrid
              articles={filteredArticles}
              items={items}
              relocationPage={relocationPage}
            />
          ) : (
            <div className="rounded-lg bg-gray-50 py-12 text-center">
              <p className="text-sm text-gray-500">
                {searchQuery
                  ? "No articles match your search"
                  : "No articles available"}
              </p>
            </div>
          )}
        </div>
      </RecordScreenCard>
    </div>
  );
}

function VirtualizedArticleGrid({
  articles,
  items,
  relocationPage,
}: {
  articles: Article[];
  items: any[];
  relocationPage: NonNullable<ReturnType<typeof useRelocationPageDrawer>>;
}) {
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: Math.ceil(articles.length / 3),
    getScrollElement: () => parentRef.current,
    estimateSize: () => 280,
    overscan: 2,
  });

  return (
    <div ref={parentRef} className="h-[600px] overflow-auto">
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const startIndex = virtualRow.index * 3;
          const rowArticles = articles.slice(startIndex, startIndex + 3);

          return (
            <div
              key={virtualRow.key}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {rowArticles.map((article) => {
                  const disabled =
                    items.findIndex(
                      (a) => a.dato_article_slug === article.slug,
                    ) > -1;

                  return (
                    <ArticleBlock
                      relocationPage={relocationPage}
                      key={article.id}
                      article={article}
                      disabled={disabled}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ArticleBlock({
  article,
  disabled,
  relocationPage,
}: {
  article: Article;
  disabled: boolean;
  relocationPage: NonNullable<ReturnType<typeof useRelocationPageDrawer>>;
}) {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useGqlMutation(
    createRelocationPageArticleMutation,
    {
      onSuccess: () => {
        return queryClient.invalidateQueries({
          predicate: (q) => q.queryKey.includes("RelocationPageRecord"),
        });
      },
    },
  );

  const handleClick = useCallback(async () => {
    if (isPending || disabled) return;

    await mutateAsync({
      input: {
        dato_article_slug: article.slug,
        relocation_page_id: relocationPage.id,
      },
    });
  }, [article.slug, disabled, isPending, mutateAsync, relocationPage.id]);

  return (
    <button
      disabled={isPending || disabled}
      onClick={handleClick}
      className={cn(
        "group relative overflow-hidden rounded-lg border",
        disabled
          ? "border-emerald-200 bg-emerald-50"
          : "shadow-xs border-gray-200 bg-white hover:border-indigo-200 hover:shadow-md",
        (isPending || disabled) && "cursor-not-allowed",
      )}
    >
      <div className="aspect-video w-full overflow-hidden">
        {article.seo.image.url ? (
          <img
            loading="lazy"
            className={cn(
              "h-full w-full object-cover",
              !(isPending || disabled) &&
                "transform-gpu duration-300 group-hover:scale-105",
              (isPending || disabled) && "opacity-80",
            )}
            src={article.seo.image.url}
            alt={article.seo.image.alt ?? ""}
          />
        ) : null}
      </div>

      {isPending ? (
        <div className="absolute inset-0 flex items-center justify-center bg-black/10">
          <Spinner className="text-white" />
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors duration-300 group-hover:bg-black/30">
          {!disabled ? (
            <div
              className={cn(
                "flex items-center justify-center gap-2 rounded-full bg-white px-3 py-1 shadow-sm",
                "opacity-0 transition-opacity duration-200 group-hover:opacity-100",
              )}
            >
              <PlusIcon className="h-4 w-4 text-indigo-600" />
              <span className="text-xs font-medium text-indigo-600">Add</span>
            </div>
          ) : (
            <div className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-700">
              Added
            </div>
          )}
        </div>
      )}

      <div className={cn("p-2", disabled ? "bg-emerald-50" : "bg-white")}>
        <p
          className={cn(
            "line-clamp-2 text-sm",
            disabled ? "text-emerald-700" : "text-gray-700",
          )}
        >
          {article.seo.title}
        </p>
      </div>
    </button>
  );
}

function RelocationPageImageTab() {
  const relocationPage = useRelocationPageDrawer();
  const { mutateAsync: createMedia } = useGqlMutation(createMediaMutation);
  const queryClient = useQueryClient();

  if (!relocationPage) {
    return null;
  }

  async function handleUpload(uploads: Upload[]) {
    const fileToUpload = uploads[0];

    await createMedia({
      input: {
        file_name: fileToUpload.file.name,
        objectId: relocationPage!.id,
        objectType: ModelType.RelocationPage,
        collectionName: MediaCollection.Images,
        key: fileToUpload.filePath,
      },
    });

    queryClient.invalidateQueries({
      predicate: (q) => q.queryKey.includes("RelocationPageRecord"),
    });
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="text-lg font-medium text-gray-900">Featured Image</h3>
        <p className="mt-1 text-sm text-gray-500">
          Upload or change the featured image for this relocation page
        </p>

        <div className="mt-6">
          {relocationPage.image ? (
            <div className="space-y-4">
              <ImoovaImage
                alt={relocationPage.title}
                media={relocationPage.image}
                className="h-64 w-full rounded-lg object-cover"
              />
              <FileUploader onUpload={handleUpload} />
            </div>
          ) : (
            <FileUploader onUpload={handleUpload} />
          )}
        </div>
      </div>
    </div>
  );
}

function RelocationPageDrawerActions({
  relocationPage,
}: {
  relocationPage: any;
}) {
  const getActions = useRelocationPageActions();

  if (!relocationPage) {
    return null;
  }

  const actionGroups = getActions(relocationPage);

  return <ActionGroupButtons buttons={actionGroups} />;
}
